import { useRef, useCallback } from 'react';

/**
 * Custom hook for debouncing function calls to reduce main thread blocking
 * Particularly useful for expensive event handlers like onClick with animations
 * 
 * @param callback - The function to debounce
 * @param delay - Delay in milliseconds before executing the callback
 * @returns Debounced function
 */
export function useDebounce<T extends (...args: any[]) => any>(
    callback: T,
    delay: number
): (...args: Parameters<T>) => void {
    const timeoutRef = useRef<NodeJS.Timeout | undefined>(undefined);

    return useCallback(
        (...args: Parameters<T>) => {
            if (timeoutRef.current) {
                clearTimeout(timeoutRef.current);
            }

            timeoutRef.current = setTimeout(() => {
                callback(...args);
            }, delay);
        },
        [delay, callback]
    );
}
